import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { AnalyticProvider } from '@application/analytic/analytic_link/mod';
import { CounterState, useCounterState } from '@application/counter';
import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { AutoFocus, Box, BreakPoints, Flex, NotificationAlert, SafeArea, useBreakpoint, VisuallyHidden } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { Screen } from '@component/screen/screen.component';
import { ViewNavBar } from '@component/view_nav_bar.component';
import type { AppointmentCaseModel } from '@feature/cases/mod';
import { useService } from '@visiba/instantiation';
import { usePresentation } from '@visiba/presentation';
import { useEffect } from 'react';
import styled from 'styled-components';
import { InfoViewAppointmentActions } from './_info_view_appointment_actions.component';
import { InfoViewAppointmentDetails } from './_info_view_appointment_details.component';
import { InfoViewAppointmentParticipant } from './_info_view_appointment_participants.component';
import { InfoViewAppointmentUnhandledActions } from './_info_view_appointment_unhandled_actions.component';

interface Props {
	model: AppointmentCaseModel;
}

const elements = {
	container: styled.div`
		background-color: ${tokens.color.bg.surface};
		width: 100%;
		overflow-x: hidden;
		overflow-y: auto;
		max-width: 100vw;
		margin-top: 40px;
		min-height: calc(100% - 40px);
		padding: ${tokens.spacing.x5} 0;
	`,
	hideWhenDesktop: styled.div`
		display: contents;

		${tokens.breakpointQuery.small} {
			display: none;
		}
	`,
	hideWhenMobile: styled.div`
		display: none;

		${tokens.breakpointQuery.small} {
			display: contents;
		}
	`,
	navBarWrapper: styled.div`
		position: absolute;
		top: 0;
		left: 0;
		height: 40px;
		width: 100%;
		z-index: 1;
	`,
	physicalLocationImage: styled.div`
		width: 100%;
		min-height: 192px;
		overflow: hidden;
		background: ${primitiveColorTokens.neutral[200]};
		border-radius: ${tokens.borderRadius.medium};
		display: flex;
		justify-content: center;
		align-items: center;

		img {
			width: 100%;
		}
	`,
	information: styled.div`
		width: 100%;
		border: 1px solid ${tokens.color.border.default};
		gap: ${tokens.spacing.x1};
		display: flex;
		padding: ${tokens.spacing.x1_5};
		border-radius: ${tokens.borderRadius.medium};
	`,
};

export function InfoViewAppointment({ model }: Props): JSX.Element {
	const presentation = usePresentation(model.presentation);
	const dateService = useService(DateService);
	const analyticService = useService(AnalyticService);
	const mobileBreakpoint = useBreakpoint(BreakPoints.Small);

	const data = model.get();

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: 'My Cases-Appointment' });
		},
		[analyticService],
	);

	useEffect(
		function whenComponentMounts() {
			model.updateExtendedData();
			model.markAsSeen();
			model.analyticQuirk_trackOnMountInVisitsView();
		},
		[model],
	);

	const workableData =
		data != null && presentation.metaData != null
			? {
					...data,
					...presentation.metaData,
				}
			: null;

	return (
		<AnalyticProvider category={AnalyticCategories.VisitsAppointment}>
			<Screen route='infoViewAppointment' canBeActivated={mobileBreakpoint} isActivated>
				<AutoFocus key={data.ticket /* Key here to get React to invalidate the v-dom diff. Forcing a rerender of this component */}>
					<VisuallyHidden>
						<h1>{withTranslation('PW.Component.Visits.AppointmentInfo.A11y.Title')}</h1>
					</VisuallyHidden>
				</AutoFocus>

				<elements.container>
					<SafeArea asChild horizontalPadding={mobileBreakpoint ? undefined : tokens.spacing.x4}>
						<Flex
							gap={tokens.spacing.x2_5}
							style={{
								minHeight: '100%',
							}}
						>
							{data.reservedUntil ? <CountDownerNotificationAlert reservedUntil={data.reservedUntil} /> : null}
							<InfoViewAppointmentDetails data={workableData} />
							<InfoViewAppointmentUnhandledActions data={workableData} model={model} />
							<InfoViewAppointmentParticipant data={workableData} />
							<InfoViewAppointmentActions data={workableData} model={model} />
						</Flex>
					</SafeArea>
				</elements.container>

				<elements.navBarWrapper>
					<ViewNavBar
						to='../../'
						analyticAction='Clicked go to My cases-back arrow'
						title={data.date.format(
							dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
						)}
					/>
				</elements.navBarWrapper>
			</Screen>
		</AnalyticProvider>
	);
}

interface CountDownerProps {
	reservedUntil: CounterState;
}

function CountDownerNotificationAlert({ reservedUntil }: CountDownerProps): JSX.Element {
	const { time } = useCounterState(reservedUntil);

	return (
		<Box>
			<NotificationAlert variant='warning'>
				{withTranslation('PW.ReservedUntil.NotificationAlert', {
					min: time.toString(),
				})}
			</NotificationAlert>
		</Box>
	);
}
